'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';

import { CategoryParamsSync, CategoryProvider } from '@eliadmin/core/src/stores/category-store';

import { SearchEmpty } from './search-empty';
import { SearchResults } from './search-results';
import { SearchSection } from './search-skeleton';
import type { SearchViewProps } from './search-types';

/** The query of the URL (`?search=`), trimmed; '' = nothing typed yet. */
function SearchRoute(props: SearchViewProps) {
  const query = useSearchParams().get('search')?.trim() ?? '';

  if (query) return <SearchResults key={query} query={query} {...props} />;

  return (
    <>
      <SearchSection breadcrumbs={props.breadcrumbs} breadcrumbsLabel={props.breadcrumbsLabel}>
        <SearchEmpty
          variant="start"
          texts={props.texts}
          panel={props.panel}
          printerBrandIds={props.printerBrandIds}
        />
      </SearchSection>
      {props.categories}
    </>
  );
}

/**
 * Client part of the search page (`pages/search/page.tsx`): the route is static, so the query is
 * read from the URL here. The page runs in its own category store – the same one the category
 * pages have – so the sort menu, the pagination and the `order`, `orderBy`, `page` URL parameters
 * work exactly as in the category listing while the rest of the URL (`search`) stays untouched.
 */
export function SearchView(props: SearchViewProps) {
  return (
    <CategoryProvider state={{ paramsReady: false }}>
      <Suspense>
        <CategoryParamsSync />
      </Suspense>
      <SearchRoute {...props} />
    </CategoryProvider>
  );
}
